import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { FiRefreshCw, FiSearch, FiFile } from 'react-icons/fi'
import api from '../../../libs/axios'
import { useDebounce } from '../../../hooks/useDebounce'
import { Pagination } from '../../../utils/pagination.utils'
import { useAuth } from '../../../provider/auth.provider'

interface Invoice {
  id: number
  po_number: string
  po_date: string
  po_document?: string
  item: string
  item_description: string
  invoice_number?: string
  invoice_date?: string
  invoice_document?: string
  invoice_basic_amount?: number | null
  invoice_gst_amount?: number | null
  invoice_total_amount?: number | null
  working_month?: string | null
  status: string
  rejection_reason?: string | null
  vendor?: { id: number; vendor_full_name: string; vendor_code?: string }
  billing_coordinator?: { id: number; first_name: string; last_name: string }
  project_manager?: { id: number; first_name: string; last_name: string } | null
  team_coordinator?: { id: number; first_name: string; last_name: string } | null
  site?: {
    id: number
    site_id?: string
    site_name?: string
    reference_number?: string
    project?: {
      id: number
      project_name: string
      activity?: { id: number; activity_name: string }
    }
  }
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  po_generated: { label: 'PO Generated', color: 'bg-blue-100 text-blue-700' },
  invoice_submitted: { label: 'Invoice Submitted', color: 'bg-amber-100 text-amber-700' },
  invoice_booked: { label: 'Invoice Booked', color: 'bg-green-100 text-green-700' },
  invoice_rejected: { label: 'Invoice Rejected', color: 'bg-red-100 text-red-700' },
}

const INVOICE_STAGE_OPTIONS = [
  { value: '', label: 'All Stages' },
  { value: 'po_generated', label: 'PO Generated' },
  { value: 'invoice_submitted', label: 'Invoice Submitted' },
  { value: 'invoice_booked', label: 'Invoice Booked' },
  { value: 'invoice_rejected', label: 'Invoice Rejected' },
]

const fmt = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

const fmtAmt = (v: number | null | undefined) =>
  v != null ? `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'

const HEADERS = [
  'Action', 'PO Date', 'PO Number', 'Project Name', 'Activity', 'Vendor Name',
  'Billing Coordinator', 'Invoice Item Name', 'Invoice Description',
  'Working Month', 'Vendor Invoice Number', 'Vendor Invoice Date',
  'Project Manager', 'Team Coordinator', 'Invoice Stage',
  'Basic Amount', 'GST Amount', 'Total Amount', 'Rejection Reason',
]

export default function SubmittedInvoicesPage() {
  const { hasPermission } = useAuth()
  const canViewVendorPoDocument = hasPermission('VendorPortal', 'view_vendor_po_document')
  const canViewVendorInvoiceDocument = hasPermission('VendorPortal', 'view_vendor_invoice_document')
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const debouncedSearch = useDebounce(search, 400)

  const fetchInvoices = useCallback(() => {
    setLoading(true)
    const params: any = { page, limit }
    if (statusFilter) params.status = statusFilter
    if (debouncedSearch) params.search = debouncedSearch
    api.get('/vendor-portal/invoices', { params })
      .then(r => {
        setInvoices(r.data.data?.rows ?? [])
        setTotal(r.data.data?.count ?? 0)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [page, limit, statusFilter, debouncedSearch])

  useEffect(() => { fetchInvoices() }, [fetchInvoices])
  useEffect(() => { setPage(1) }, [debouncedSearch])

  return (
    <>
      <Helmet><title>Submitted Invoices – Vendor Portal</title></Helmet>
      <div className="space-y-4">
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Submitted Invoices</h2>
              <p className="text-sm text-gray-600 mt-1">{total} invoice{total !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex gap-2 items-center flex-wrap">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search site / ref..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 w-56"
                />
                <FiSearch className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
              </div>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
                className="px-3 py-2 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500"
              >
                {INVOICE_STAGE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <button onClick={fetchInvoices} className="p-2 border border-gray-300 rounded hover:bg-gray-50 text-gray-600">
                <FiRefreshCw className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {HEADERS.map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}>{HEADERS.map((_, j) => <td key={j} className="px-3 py-3"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
                  ))
                ) : invoices.length === 0 ? (
                  <tr><td colSpan={HEADERS.length} className="px-4 py-10 text-center text-gray-400">No submitted invoices found</td></tr>
                ) : invoices.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50">
                    {/* Action */}
                    <td className="px-3 py-3">
                      {canViewVendorPoDocument && inv.po_document ? (
                        <a
                          href={inv.po_document}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 underline whitespace-nowrap mb-1"
                          title="View Vendor PO Document"
                        >
                          <FiFile className="w-3 h-3" /> PO Doc
                        </a>
                      ) : null}
                      {canViewVendorInvoiceDocument && inv.invoice_document ? (
                        <a
                          href={inv.invoice_document}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 underline whitespace-nowrap"
                          title="View Vendor Invoice Document"
                        >
                          <FiFile className="w-3 h-3" /> Invoice Doc
                        </a>
                       ) : null}
                        {(!canViewVendorPoDocument || !inv.po_document) && (!canViewVendorInvoiceDocument || !inv.invoice_document) && (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                    </td>
                    <td className="px-3 py-3 text-gray-600 whitespace-nowrap text-xs">{fmt(inv.po_date)}</td>
                    <td className="px-3 py-3 font-mono font-semibold text-blue-700 text-xs whitespace-nowrap">{inv.po_number}</td>
                    <td className="px-3 py-3 text-gray-900 text-xs whitespace-nowrap">{inv.site?.project?.project_name || '—'}</td>
                    <td className="px-3 py-3 text-gray-500 text-xs">{inv.site?.project?.activity?.activity_name || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">
                      {inv.vendor ? (
                        <div>
                          <div>{inv.vendor.vendor_full_name}</div>
                          {inv.vendor.vendor_code && <div className="text-gray-400 text-xs">{inv.vendor.vendor_code}</div>}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {inv.billing_coordinator ? `${inv.billing_coordinator.first_name} ${inv.billing_coordinator.last_name}` : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-700 font-medium">{inv.item || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-500 max-w-[160px] truncate" title={inv.item_description || ''}>{inv.item_description || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-700">{inv.working_month || '—'}</td>
                    <td className="px-3 py-3 font-mono text-xs text-gray-900">{inv.invoice_number || '—'}</td>
                    <td className="px-3 py-3 text-xs text-gray-600 whitespace-nowrap">{fmt(inv.invoice_date)}</td>
                    <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {inv.project_manager ? `${inv.project_manager.first_name} ${inv.project_manager.last_name}` : '—'}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {inv.team_coordinator ? `${inv.team_coordinator.first_name} ${inv.team_coordinator.last_name}` : '—'}
                    </td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[inv.status]?.color ?? 'bg-gray-100 text-gray-600'}`}>
                        {STATUS_CONFIG[inv.status]?.label ?? inv.status}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtAmt(inv.invoice_basic_amount)}</td>
                    <td className="px-3 py-3 text-xs text-gray-700 whitespace-nowrap">{fmtAmt(inv.invoice_gst_amount)}</td>
                    <td className="px-3 py-3 text-xs font-medium text-gray-900 whitespace-nowrap">{fmtAmt(inv.invoice_total_amount)}</td>
                    <td className="px-3 py-3 text-xs max-w-[200px]">
                      {inv.status === 'invoice_rejected' && inv.rejection_reason ? (
                        <span className="text-red-600" title={inv.rejection_reason}>{inv.rejection_reason}</span>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            pagination={{
              page,
              limit,
              total_count: total,
              total_pages: Math.ceil(total / limit) || 1,
              has_next_page: page * limit < total,
              has_prev_page: page > 1,
              next_page: page * limit < total ? page + 1 : null,
              prev_page: page > 1 ? page - 1 : null,
              offset: (page - 1) * limit,
            }}
            onPageChange={p => setPage(p)}
            onLimitChange={l => { setLimit(l); setPage(1) }}
          />
        </div>
      </div>
    </>
  )
}
